import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Embers } from "@/components/Embers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

const TIGER_SIDE = "https://static.prod-images.emergentagent.com/jobs/0f0ac7ba-60f1-492a-a009-cff6f774132c/images/cd09731b7fd41156e52c1dfd322676aeee774848a100393d5dfdecccdc64efbf.png";

const AuthLayout = ({ children, title, subtitle }) => (
  <div className="min-h-screen flex bg-[#050505] text-[#f5f5f5] relative overflow-hidden">
    <Embers count={18} />
    <div className="absolute -top-32 -left-32 w-[460px] h-[460px] bg-[#ff7a00]/15 rounded-full blur-[140px]" />

    <div className="flex-1 flex flex-col p-6 sm:p-10 lg:p-16 relative z-10">
      <div className="flex items-center justify-between">
        <Logo />
        <Link to="/" className="text-[#8a8a8a] text-sm hover:text-[#ff9a2f] inline-flex items-center gap-2" data-testid="auth-back">
          <ArrowLeft size={16} /> Back home
        </Link>
      </div>
      <div className="flex-1 flex items-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{title}</h1>
          <p className="text-[#8a8a8a] mt-2 text-sm">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </motion.div>
      </div>
    </div>

    <div className="hidden lg:block w-[44%] xl:w-[42%] relative">
      <img src={TIGER_SIDE} alt="Tiger Warrior" className="absolute inset-0 w-full h-full object-cover" data-testid="auth-tiger-image" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      <div className="absolute bottom-10 left-10 right-10">
        <div className="glass rounded-xl p-5">
          <div className="text-[#8a8a8a] text-[10px] tracking-[0.3em] uppercase">Alpha Trader</div>
          <div className="font-display text-xl font-bold mt-1">"The hunt rewards the disciplined."</div>
        </div>
      </div>
    </div>
  </div>
);

export const LoginPage = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${u.first_name || u.email.split("@")[0]}`);
      const dest = loc.state?.from?.pathname || (u.roles?.includes("admin") ? "/admin" : "/dashboard");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Enter the pack." subtitle="Log in to your Funded Index account.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">Email</Label>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="enter your email address"
            className="mt-2 bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 focus-visible:border-[#ff7a00] focus-visible:ring-[#ff7a00]/30"
            data-testid="login-email-input"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">Password</Label>
            <Link to="/forgot-password" className="text-xs text-[#ff9a2f] hover:text-[#ff7a00]" data-testid="login-forgot-link">Forgot?</Link>
          </div>
          <div className="relative mt-2">
            <Input
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 pr-12 focus-visible:border-[#ff7a00] focus-visible:ring-[#ff7a00]/30"
              data-testid="login-password-input"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a8a8a] hover:text-[#ff9a2f]" data-testid="login-show-password">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={busy} className="w-full h-12 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold glow-orange-soft" data-testid="login-submit">
          {busy ? "Authenticating..." : "Log in"}
        </Button>
        <div className="text-center text-sm text-[#8a8a8a]">
          New trader?{" "}
          <Link to="/signup" className="text-[#ff9a2f] hover:text-[#ff7a00] font-semibold" data-testid="login-signup-link">Create account</Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export const SignupPage = () => {
  const { signup } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [refCode, setRefCode] = useState("");
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", country: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Pick up ?ref= from URL or from previously-stashed value
  React.useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const urlRef = params.get("ref");
    if (urlRef) {
      const clean = urlRef.trim().toUpperCase();
      setRefCode(clean);
      try { localStorage.setItem("fi_ref", clean); } catch {}
      return;
    }
    try {
      const stashed = localStorage.getItem("fi_ref");
      if (stashed) setRefCode(stashed);
    } catch {}
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, email: form.email.trim().toLowerCase() };
      if (refCode) payload.referral_code = refCode;
      const u = await signup(payload);
      try { localStorage.removeItem("fi_ref"); } catch {}
      toast.success(`Welcome to the pack, ${u.first_name || u.email.split("@")[0]}`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Join the pack." subtitle="Create your Funded Index account in seconds.">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        {refCode && (
          <div className="glass rounded-lg px-4 py-2.5 flex items-center gap-2 text-xs" data-testid="signup-ref-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a00] animate-pulse" />
            <span className="text-[#8a8a8a]">Referred by:</span>
            <span className="text-[#ff9a2f] font-mono font-semibold">{refCode}</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">First Name</Label>
            <Input value={form.first_name} onChange={set("first_name")} className="mt-2 bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 focus-visible:border-[#ff7a00] focus-visible:ring-[#ff7a00]/30" required data-testid="signup-first-name" />
          </div>
          <div>
            <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">Last Name</Label>
            <Input value={form.last_name} onChange={set("last_name")} className="mt-2 bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 focus-visible:border-[#ff7a00] focus-visible:ring-[#ff7a00]/30" data-testid="signup-last-name" />
          </div>
        </div>
        <div>
          <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">Country</Label>
          <Input value={form.country} onChange={set("country")} className="mt-2 bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 focus-visible:border-[#ff7a00] focus-visible:ring-[#ff7a00]/30" data-testid="signup-country" placeholder="e.g., India" />
        </div>
        <div>
          <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">Email</Label>
          <Input type="email" required value={form.email} onChange={set("email")} className="mt-2 bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 focus-visible:border-[#ff7a00] focus-visible:ring-[#ff7a00]/30" data-testid="signup-email" />
        </div>
        <div>
          <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">Password</Label>
          <div className="relative mt-2">
            <Input type={show ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")} className="bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 pr-12 focus-visible:border-[#ff7a00] focus-visible:ring-[#ff7a00]/30" data-testid="signup-password" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a8a8a] hover:text-[#ff9a2f]">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="text-[10px] text-[#8a8a8a] mt-1">Minimum 6 characters.</div>
        </div>
        <Button type="submit" disabled={busy} className="w-full h-12 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold glow-orange-soft" data-testid="signup-submit">
          {busy ? "Creating account..." : "Create account"}
        </Button>
        <div className="text-center text-sm text-[#8a8a8a]">
          Already a member?{" "}
          <Link to="/login" className="text-[#ff9a2f] hover:text-[#ff7a00] font-semibold" data-testid="signup-login-link">Log in</Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("If an account exists, a reset email has been sent.");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Reset password." subtitle="We'll send a secure reset link if your email is registered.">
      {sent ? (
        <div className="glass rounded-xl p-6 text-sm text-[#cfcfcf]" data-testid="forgot-sent-message">
          Check your inbox. The reset link expires in 1 hour.
          <div className="mt-4">
            <Link to="/login" className="text-[#ff9a2f] hover:text-[#ff7a00] font-semibold">Back to login</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <div>
            <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12 focus-visible:border-[#ff7a00]" data-testid="forgot-email-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-12 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="forgot-submit">
            {busy ? "Sending..." : "Send reset link"}
          </Button>
          <div className="text-center text-sm text-[#8a8a8a]">
            <Link to="/login" className="text-[#ff9a2f] hover:text-[#ff7a00]">Back to login</Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
};

export const ResetPasswordPage = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. Log in with your new password.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Set new password." subtitle="Enter your new password below.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div>
          <Label className="text-[#cfcfcf] text-xs uppercase tracking-wider">New Password</Label>
          <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-[#0a0a0a] border-[#ff7a00]/20 text-[#f5f5f5] h-12" data-testid="reset-password-input" />
        </div>
        <Button type="submit" disabled={busy} className="w-full h-12 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="reset-submit">
          {busy ? "Resetting..." : "Reset password"}
        </Button>
      </form>
    </AuthLayout>
  );
};
